import { type ConsentState, CONSENT_LOCAL_KEY } from "./cookieConsent.types";

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const CONSENT_COOKIE_KEY = CONSENT_LOCAL_KEY;
const PUBLIC_SUFFIX_PREFIXES = new Set([
  "ac",
  "co",
  "com",
  "edu",
  "gov",
  "net",
  "org",
]);

const parseConsentState = (raw: string | null): Partial<ConsentState> => {
  if (!raw) {
    return {};
  }

  try {
    return JSON.parse(raw) as Partial<ConsentState>;
  } catch {
    return {};
  }
};

const getCookieValue = (name: string): string | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const encodedName = `${encodeURIComponent(name)}=`;
  const cookie = document.cookie
    .split(";")
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(encodedName));

  if (!cookie) {
    return null;
  }

  return decodeURIComponent(cookie.slice(encodedName.length));
};

const getSharedCookieDomain = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const { hostname } = window.location;

  if (!hostname || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
    return undefined;
  }

  const parts = hostname.split(".").filter(Boolean);

  if (parts.length < 2) {
    return undefined;
  }

  if (hostname.endsWith(".localhost")) {
    return ".localhost";
  }

  const [topLevelDomain, secondLevelDomain] = parts.slice(-2).reverse();
  const domainPartCount =
    topLevelDomain.length === 2 &&
    PUBLIC_SUFFIX_PREFIXES.has(secondLevelDomain) &&
    parts.length >= 3
      ? 3
      : 2;

  return `.${parts.slice(-domainPartCount).join(".")}`;
};

const writeConsentCookie = (state: ConsentState, domain?: string): void => {
  if (typeof document === "undefined") {
    return;
  }

  const value = encodeURIComponent(JSON.stringify(state));
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const domainPart = domain ? `; Domain=${domain}` : "";

  document.cookie = `${encodeURIComponent(
    CONSENT_COOKIE_KEY,
  )}=${value}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}${domainPart}`;
};

/**
 * Load consent from local storage and the shared-domain cookie.
 * @returns The newest accepted consent state from browser storage.
 */
export const loadPersistedConsent = (): Partial<ConsentState> => {
  try {
    if (typeof window === "undefined") {
      return {};
    }

    const localState = parseConsentState(
      window.localStorage.getItem(CONSENT_LOCAL_KEY),
    );
    const cookieState = parseConsentState(getCookieValue(CONSENT_COOKIE_KEY));

    if (!cookieState.consentGiven) {
      return localState;
    }

    if (!localState.consentGiven) {
      return cookieState;
    }

    const localTime = localState.lastUpdated
      ? new Date(localState.lastUpdated).getTime()
      : 0;
    const cookieTime = cookieState.lastUpdated
      ? new Date(cookieState.lastUpdated).getTime()
      : 0;

    return cookieTime >= localTime ? cookieState : localState;
  } catch {
    return {};
  }
};

/**
 * Persist consent locally and to a parent-domain cookie for sibling subdomains.
 * @param state Current consent state.
 * @returns {void}
 */
export const persistConsent = (state: ConsentState): void => {
  try {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(CONSENT_LOCAL_KEY, JSON.stringify(state));
    writeConsentCookie(state);

    const sharedDomain = getSharedCookieDomain();
    if (sharedDomain) {
      writeConsentCookie(state, sharedDomain);
    }
  } catch {
    // Storage/cookies may be unavailable in restricted browser contexts.
  }
};
